import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRunTrace,
  GetRunTraceError,
  runConversation,
  type RunRequest,
  type RunResult,
  type RunTraceResult,
} from '@pypestream/testview/browser';

import { buildEngagementApiUrl } from '../../utils';

/**
 * Runs a test conversation — IN THE BROWSER.
 *
 * It used to `POST /test/run` and let a server do it. That route could
 * authenticate a caller but never authorize one: it takes an `appId`, and a
 * channel id is not a secret — it sits in the embed snippet on the customer's
 * own public website — so any signed-in user could have driven real, billable
 * conversations on another customer's LIVE channel. It was closed to localhost
 * rather than shipped, which is why Run worked on one machine and was disabled
 * everywhere else.
 *
 * Doing it here dissolves that instead of guarding it. The page opens an
 * ordinary anonymous session against the channel — precisely what the embedded
 * widget does on the customer's site, and what any visitor's browser may
 * already do. There is no privileged server in the path, so there is no
 * privilege to escalate, and Run works wherever Pro Studio does.
 *
 * It is NOT the widget's own conversation: `SessionInfo` from the Loader
 * carries `chatId` but no access token, by design, so a second client cannot
 * join it. This opens its own — which is why a run does not appear in the live
 * pane on the left.
 */

export type RunState = 'idle' | 'running' | 'error';

/**
 * `idle`: no run has completed yet, so there is nothing to fetch a trace for.
 * `loading`: the run finished and the trace fetch is in flight — it can take
 * up to the server's own retry budget (~15s), since Langfuse's OTel ingestion
 * lags the conversation itself.
 * `ready`: the fetch answered. A `RunTraceResult` with an empty `spans` array
 * is still `ready` — that is "looked, and found none" — see `unavailable` for
 * "couldn't look at all".
 * `unavailable`: the fetch itself failed (closed to this host, or Langfuse
 * isn't configured here) — a fact about the DEPLOYMENT, not this run.
 */
export type TraceState = 'idle' | 'loading' | 'ready' | 'unavailable';

/**
 * How long a whole run may take. Bounds the socket handshake, the greeting and
 * every turn — the same budget the server route used to apply, kept because a
 * tab left open on a silent channel should stop rather than hang.
 */
const RUN_TIMEOUT_MS = 60_000;

/**
 * @param target The channel a run would go against, as a stable key
 *   (`appId:channelEnv`). A previous channel's transcript must not sit under
 *   the new channel's header: `RunResults` labels the run with whatever channel
 *   is selected NOW, so leaving the old result on screen attributes a
 *   conversation to a channel it never touched.
 */
export const useTestApi = (target: string | null) => {
  const [runState, setRunState] = useState<RunState>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [traceState, setTraceState] = useState<TraceState>('idle');
  const [trace, setTrace] = useState<RunTraceResult | null>(null);
  const [traceMessage, setTraceMessage] = useState<string | null>(null);

  // The target a run was STARTED for, and the target selected NOW. Clearing
  // state when the channel changes is not enough on its own: a run already in
  // flight resolves afterwards and would write its transcript under the new
  // channel's header — the exact misattribution this is here to prevent. So
  // every write below is gated on the target being unchanged.
  const currentTarget = useRef(target);
  currentTarget.current = target;

  /**
   * The run in flight, so it can be STOPPED and not merely ignored.
   *
   * Dropping the result of a superseded run is not enough now that the run
   * happens here: it holds an open WebSocket and a real, billable conversation
   * on the channel the user just navigated away from, and it keeps both for up
   * to the whole budget. Worse, the effect below re-enables the button the
   * moment the channel changes, so a user switching channels twice could have
   * three live sockets talking to three solutions at once.
   */
  const inFlight = useRef<AbortController | null>(null);

  /** Same reasoning as `inFlight`, one level down: a trace fetch superseded
   *  by a new run (or a channel change) must stop being written to state, not
   *  merely have its answer ignored — otherwise a slow first fetch can land
   *  AFTER a second run's own trace and overwrite it. */
  const traceInFlight = useRef<AbortController | null>(null);

  // Not folded into `run`: the channel can change with no run in flight, and
  // the stale transcript is on screen from that moment. The cleanup also runs
  // on unmount, which is the other way a run is abandoned.
  useEffect(() => {
    setResult(null);
    setMessage(null);
    setRunState('idle');
    setTrace(null);
    setTraceMessage(null);
    setTraceState('idle');

    return () => {
      inFlight.current?.abort();
      inFlight.current = null;
      traceInFlight.current?.abort();
      traceInFlight.current = null;
    };
  }, [target]);

  const run = useCallback(async (request: RunRequest) => {
    const startedFor = currentTarget.current;
    const stillCurrent = () => currentTarget.current === startedFor;

    setRunState('running');
    setMessage(null);
    // Cleared up front: a previous run's transcript sitting under a new
    // request reads as this run's result.
    setResult(null);
    setTrace(null);
    setTraceMessage(null);
    setTraceState('idle');
    // Abort a still-running fetch for the PREVIOUS run — its answer, however
    // it resolves, belongs to a conversation this pane no longer shows.
    traceInFlight.current?.abort();
    traceInFlight.current = null;

    // Bounds the Langfuse query this run's trace fetch makes: traces from
    // before the conversation even started are never this run's. A few
    // seconds of slack for the gap between this line and the socket actually
    // opening is cheaper than missing the run's own first trace to clock
    // skew.
    const runStartedAtMs = Date.now() - 5_000;

    // The same URL the live widget is pointed at, derived from the page's own
    // origin — so the run and the pane beside it always reach the same stack.
    const engagementApiUrl = buildEngagementApiUrl(
      window.location.origin,
      request.channelEnv
    );

    // An owned controller rather than `AbortSignal.timeout`: the budget is one
    // of two reasons to stop, and the other — the user leaving this channel —
    // has no signal of its own. Cleared in `finally` so a finished run does not
    // leave a timer holding a reference to it.
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    const budget = setTimeout(() => controller.abort(), RUN_TIMEOUT_MS);

    /**
     * This run has been abandoned — superseded by a later one, or aborted by
     * the channel change / unmount that cleared `inFlight`.
     *
     * `stillCurrent()` alone is not enough: it compares the TARGET, not the
     * run. The runner only observes the signal between steps (the socket
     * handshake does not observe it at all), so an abort can surface seconds
     * later — by which time the user may be back on the channel they started
     * on, and the abandoned run's rejection would raise a red "Fail" card over
     * a view that has no run in it.
     */
    const abandoned = () =>
      controller.signal.aborted || inFlight.current !== controller;

    try {
      const conversation = await runConversation({
        appId: request.appId,
        engagementApiUrl,
        // One turn: the prompt is sent verbatim as the first user message.
        // Turning a plain-English instruction into a multi-turn conversation
        // needs a model driving it, which is a separate decision.
        messages: [request.prompt.trim()],
        signal: controller.signal,
      });

      if (abandoned() || !stillCurrent()) return;

      setRunState('idle');
      setResult({
        status: 'complete',
        chatId: conversation.chatId,
        engagementApiUrl: conversation.engagementApiUrl,
        transcript: conversation.transcript,
        greetingMissing: conversation.greetingMissing,
        detail:
          'The prompt was sent verbatim as one user message. Nothing ' +
          'evaluates the replies yet — read the transcript.',
      });
      setMessage(
        'The run completed. Nothing evaluates the replies yet, so a completed ' +
          'run is not a passing one.'
      );

      // Fire-and-forget, relative to the run itself: the transcript above is
      // already usable evidence, and the pipeline breakdown fills in
      // whenever Langfuse's ingestion catches up (up to the server's own
      // ~15s retry budget) without blocking it.
      setTraceState('loading');
      const traceController = new AbortController();
      traceInFlight.current = traceController;
      void getRunTrace({
        chatId: conversation.chatId,
        sinceMs: runStartedAtMs,
        signal: traceController.signal,
      })
        .then((traceResult) => {
          if (traceInFlight.current !== traceController) return;
          setTraceState('ready');
          setTrace(traceResult);
        })
        .catch((err) => {
          if (traceInFlight.current !== traceController) return;
          setTraceState('unavailable');
          setTraceMessage(
            err instanceof GetRunTraceError
              ? (err.detail ?? err.message)
              : 'Could not fetch the trace for this run.'
          );
        });
    } catch (err) {
      if (abandoned() || !stillCurrent()) return;

      setRunState('error');
      // Every error the runner throws is an `Error` — `EngagementSessionError`
      // and `ConversationError` among them — and each already names the
      // decisive fact: an unpublished channel, the wrong stack, a budget spent.
      // So the message is surfaced as written rather than wrapped in something
      // vaguer. The fallback is for a throw that is not an Error at all.
      setMessage(
        err instanceof Error ? err.message : 'The run could not be started.'
      );
    } finally {
      clearTimeout(budget);
      if (inFlight.current === controller) inFlight.current = null;
    }
  }, []);

  // No `canRun`. It used to carry the server's `capabilities.run`, which was
  // `isLocalHost(host)` — the reason the button was dead on every deployed
  // build. A run now needs a channel and a prompt and nothing else, and a
  // constant-true flag would only be a place for a stale gate to hide.
  return { runState, message, result, run, traceState, trace, traceMessage };
};
